/**
 * Sistema di impersonazione basato su localStorage
 * Separato dal sistema di autenticazione principale per evitare conflitti
 */

interface UserData {
  id: number
  name: string
  surname: string
  email: string
  role: string
  referral?: string
  folderToken?: string
}

interface ImpersonationData {
  active: boolean
  originalAdmin: UserData
  targetUser: UserData
  startedAt: string
}

interface StartImpersonationResponse {
  success: boolean
  originalAdmin: UserData
  targetUser: UserData
  message?: string
}

const STORAGE_KEY = 'quickbooking_impersonation'
const COOKIE_KEY = 'qb_impersonation'

export const useImpersonation = () => {
  // Stato reattivo per l'impersonazione
  const impersonationState = ref<ImpersonationData | null>(null)

  // Flag per tracciare se l'inizializzazione è già avvenuta
  const isInitialized = ref(false)

  /**
   * Inizializza lo stato dell'impersonazione
   */
  const initializeState = () => {
    // Evita inizializzazioni multiple
    if (isInitialized.value) return

    if (import.meta.client) {
      // Client-side: leggi da localStorage
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        try {
          const data = JSON.parse(stored) as ImpersonationData
          // Verifica che non sia scaduto (max 1 ora)
          const startedAt = new Date(data.startedAt)
          const now = new Date()
          const diffHours = (now.getTime() - startedAt.getTime()) / (1000 * 60 * 60)

          if (diffHours < 1 && data.active) {
            impersonationState.value = data
            // Sincronizza con cookie per SSR
            syncToCookie(data)
          } else {
            // Scaduto, rimuovi
            clearImpersonation()
          }
        } catch (error) {
          console.error('Error parsing impersonation data:', error)
          clearImpersonation()
        }
      }
      isInitialized.value = true
    }
  }

  /**
   * Inizializzazione lazy - viene chiamata automaticamente quando necessario
   */
  const ensureInitialized = () => {
    if (!isInitialized.value && import.meta.client) {
      initializeState()
    }
  }

  /**
   * Sincronizza lo stato con i cookies per SSR
   */
  const syncToCookie = (data: ImpersonationData | null) => {
    if (import.meta.client) {
      const cookieValue = data ? JSON.stringify(data) : ''
      document.cookie = `${COOKIE_KEY}=${encodeURIComponent(cookieValue)}; path=/; max-age=${60 * 60}` // 1 ora
    }
  }

  /**
   * Controlla se l'utente corrente è in modalità impersonazione
   */
  const isImpersonating = computed(() => {
    ensureInitialized()
    return !!(impersonationState.value?.active)
  })

  /**
   * Ottiene i dati dell'admin originale che sta impersonando
   */
  const originalAdmin = computed(() => {
    ensureInitialized()
    return impersonationState.value?.originalAdmin || null
  })

  /**
   * Ottiene i dati dell'utente impersonato
   */
  const targetUser = computed(() => {
    ensureInitialized()
    return impersonationState.value?.targetUser || null
  })

  /**
   * Avvia l'impersonazione di un utente
   */
  const startImpersonation = async (userId: number) => {
    try {
      // Chiama l'API per avviare l'impersonazione
      const response = await $fetch<StartImpersonationResponse>('/api/admin/impersonate/start', {
        method: 'POST',
        body: { userId },
      })

      if (response.success) {
        // Reset delle stores prima di iniziare l'impersonazione
        const { resetForImpersonation } = useStoreReset()
        resetForImpersonation()

        const impersonationData: ImpersonationData = {
          active: true,
          originalAdmin: response.originalAdmin,
          targetUser: response.targetUser,
          startedAt: new Date().toISOString(),
        }

        // Salva in localStorage
        if (import.meta.client) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(impersonationData))
        }

        // Aggiorna stato reattivo
        impersonationState.value = impersonationData

        // Sincronizza con cookie
        syncToCookie(impersonationData)

        return { success: true }
      }

      throw new Error(response.message || 'Failed to start impersonation')
    } catch (error) {
      console.error('Error starting impersonation:', error)
      throw error
    }
  }

  /**
   * Termina l'impersonazione e torna all'admin originale
   */
  const stopImpersonation = async () => {
    try {
      // Reset delle stores prima di terminare l'impersonazione
      const { resetForImpersonation } = useStoreReset()
      resetForImpersonation()

      // Chiama l'API per terminare l'impersonazione
      await $fetch('/api/admin/impersonate/stop', {
        method: 'POST',
      })

      // Pulisci lo stato locale
      clearImpersonation()

      return { success: true }
    } catch (error) {
      console.error('Error stopping impersonation:', error)
      // Anche in caso di errore API, pulisci lo stato locale
      clearImpersonation()
      throw error
    }
  }

  /**
   * Pulisce tutti i dati di impersonazione
   */
  const clearImpersonation = () => {
    impersonationState.value = null

    if (import.meta.client) {
      localStorage.removeItem(STORAGE_KEY)
      // Rimuovi cookie
      document.cookie = `${COOKIE_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC`
    }
  }

  /**
   * Ottiene lo stato di impersonazione dal cookie (per SSR)
   */
  const getImpersonationFromCookie = (cookieHeader?: string): ImpersonationData | null => {
    if (!cookieHeader) return null

    try {
      const cookies = cookieHeader.split(';')
      const impersonationCookie = cookies.find(cookie =>
        cookie.trim().startsWith(`${COOKIE_KEY}=`),
      )

      if (impersonationCookie) {
        const value = impersonationCookie.split('=')[1]
        const decoded = decodeURIComponent(value)
        if (decoded) {
          const data = JSON.parse(decoded) as ImpersonationData

          // Verifica che non sia scaduto
          const startedAt = new Date(data.startedAt)
          const now = new Date()
          const diffHours = (now.getTime() - startedAt.getTime()) / (1000 * 60 * 60)

          if (diffHours < 1 && data.active) {
            return data
          }
        }
      }
    } catch (error) {
      console.error('Error parsing impersonation cookie:', error)
    }

    return null
  }

  return {
    isImpersonating,
    originalAdmin,
    targetUser,
    startImpersonation,
    stopImpersonation,
    clearImpersonation,
    getImpersonationFromCookie,
    initializeState,
    impersonationState: readonly(impersonationState),
  }
}
